import React, { useEffect, useState } from 'react';
import { Form, Row, Col, Button, Spinner, Alert } from 'react-bootstrap';
import { useLanguage } from '../../context/LanguageProvider/LanguageProvider';
import { fetchSettings, updateSettings } from '../../api/settings';
import { uploadFile } from '../../api/upload';

export default function AdminSettingsPage() {
  const { t } = useLanguage();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchSettings()
      .then((data) => {
        if (!cancelled) setSettings(data.settings || {});
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'load_error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  function handleChange(key, value) {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      const payload = {
        smtpHost: settings.smtpHost || '',
        smtpPort: Number(settings.smtpPort) || 587,
        smtpUser: settings.smtpUser || '',
        smtpPass: settings.smtpPass || '',
        smtpSecure: Boolean(settings.smtpSecure),
        smtpFrom: settings.smtpFrom || '',
        contactToEmail: settings.contactToEmail || '',
        siteLogoUrl: settings.siteLogoUrl || '',
        siteFaviconUrl: settings.siteFaviconUrl || '',
        footerEmail: settings.footerEmail || '',
        contactEmail: settings.contactEmail || '',
        contactPhone: settings.contactPhone || '',
        contactAddressLine1: settings.contactAddressLine1 || '',
        contactAddressLine2: settings.contactAddressLine2 || '',
        companyName: settings.companyName || '',
        businessHours: settings.businessHours || '',
        mapEmbedUrl: settings.mapEmbedUrl || '',
        socialLinkedin: settings.socialLinkedin || '',
        socialFacebook: settings.socialFacebook || '',
        socialWhatsapp: settings.socialWhatsapp || '',
        socialInstagram: settings.socialInstagram || '',
        socialTwitter: settings.socialTwitter || '',
      };
      await updateSettings(payload);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err?.message || 'save_error');
    } finally {
      setSaving(false);
    }
  }

  async function handleUploadLogo(file) {
    if (!file) return;
    setUploadingLogo(true);
    setError('');
    try {
      const data = await uploadFile(file);
      setSettings((prev) => ({ ...prev, siteLogoUrl: data.url }));
    } catch (err) {
      setError(err?.message || 'upload_error');
    } finally {
      setUploadingLogo(false);
    }
  }

  async function handleUploadFavicon(file) {
    if (!file) return;
    setUploadingFavicon(true);
    setError('');
    try {
      const data = await uploadFile(file);
      setSettings((prev) => ({ ...prev, siteFaviconUrl: data.url }));
    } catch (err) {
      setError(err?.message || 'upload_error');
    } finally {
      setUploadingFavicon(false);
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div>
      <h2 className="mb-4">{t('admin.settings.title') || 'Settings'}</h2>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(false)}>
          {t('admin.settings.saved') || 'Settings saved successfully.'}
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <h5 className="mb-3">{t('admin.settings.smtpTitle') || 'SMTP Configuration'}</h5>
        <Row className="gy-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('admin.settings.smtpHost') || 'SMTP Host'}</Form.Label>
              <Form.Control
                type="text"
                value={settings.smtpHost || ''}
                onChange={(e) => handleChange('smtpHost', e.target.value)}
                placeholder="smtp.gmail.com"
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>{t('admin.settings.smtpPort') || 'Port'}</Form.Label>
              <Form.Control
                type="number"
                value={settings.smtpPort || 587}
                onChange={(e) => handleChange('smtpPort', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>{t('admin.settings.smtpSecure') || 'Secure (SSL)'}</Form.Label>
              <Form.Select
                value={settings.smtpSecure ? 'true' : 'false'}
                onChange={(e) => handleChange('smtpSecure', e.target.value === 'true')}
              >
                <option value="false">false</option>
                <option value="true">true</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('admin.settings.smtpUser') || 'SMTP User'}</Form.Label>
              <Form.Control
                type="text"
                value={settings.smtpUser || ''}
                onChange={(e) => handleChange('smtpUser', e.target.value)}
                placeholder="user@gmail.com"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('admin.settings.smtpPass') || 'SMTP Password'}</Form.Label>
              <Form.Control
                type="password"
                value={settings.smtpPass || ''}
                onChange={(e) => handleChange('smtpPass', e.target.value)}
                placeholder="••••••••"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('admin.settings.smtpFrom') || 'From Email'}</Form.Label>
              <Form.Control
                type="email"
                value={settings.smtpFrom || ''}
                onChange={(e) => handleChange('smtpFrom', e.target.value)}
                placeholder="noreply@jassp.com"
              />
            </Form.Group>
          </Col>
        </Row>

        <h5 className="mt-4 mb-3">{t('admin.settings.contactTitle') || 'Contact Form'}</h5>
        <Row className="gy-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('admin.settings.contactToEmail') || 'Recipient Email'}</Form.Label>
              <Form.Control
                type="email"
                value={settings.contactToEmail || ''}
                onChange={(e) => handleChange('contactToEmail', e.target.value)}
                placeholder="direction@jassp.com"
              />
            </Form.Group>
          </Col>
        </Row>

        <h5 className="mt-4 mb-3">{t('admin.settings.brandingTitle') || 'Branding'}</h5>
        <Row className="gy-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('admin.settings.siteLogo') || 'Site Logo'}</Form.Label>
              <div className="mb-2">
                {settings.siteLogoUrl ? (
                  <img src={settings.siteLogoUrl} alt="logo preview" style={{ maxHeight: 48 }} />
                ) : (
                  <span className="text-muted small">{t('admin.settings.noLogo') || 'No logo uploaded'}</span>
                )}
              </div>
              <Form.Control
                type="file"
                accept="image/*"
                disabled={uploadingLogo}
                onChange={(e) => handleUploadLogo(e.target.files?.[0])}
              />
              {uploadingLogo && <Spinner size="sm" animation="border" className="ms-2" />}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('admin.settings.siteFavicon') || 'Favicon'}</Form.Label>
              <div className="mb-2">
                {settings.siteFaviconUrl ? (
                  <img src={settings.siteFaviconUrl} alt="favicon preview" style={{ maxHeight: 32 }} />
                ) : (
                  <span className="text-muted small">{t('admin.settings.noFavicon') || 'No favicon uploaded'}</span>
                )}
              </div>
              <Form.Control
                type="file"
                accept="image/x-icon,image/png,image/jpeg,.ico"
                disabled={uploadingFavicon}
                onChange={(e) => handleUploadFavicon(e.target.files?.[0])}
              />
              {uploadingFavicon && <Spinner size="sm" animation="border" className="ms-2" />}
            </Form.Group>
          </Col>
        </Row>

        <h5 className="mt-4 mb-3">{t('admin.settings.contactDetailsTitle') || 'Contact Details'}</h5>
        <Row className="gy-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('admin.settings.companyName') || 'Company Name'}</Form.Label>
              <Form.Control
                type="text"
                value={settings.companyName || ''}
                onChange={(e) => handleChange('companyName', e.target.value)}
                placeholder="JASSP SAS"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('admin.settings.footerEmail') || 'Footer Email'}</Form.Label>
              <Form.Control
                type="email"
                value={settings.footerEmail || ''}
                onChange={(e) => handleChange('footerEmail', e.target.value)}
                placeholder="direction@jassp.com"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('admin.settings.contactEmail') || 'Contact Page Email'}</Form.Label>
              <Form.Control
                type="email"
                value={settings.contactEmail || ''}
                onChange={(e) => handleChange('contactEmail', e.target.value)}
                placeholder="contact@jassp.com"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('admin.settings.contactPhone') || 'Phone'}</Form.Label>
              <Form.Control
                type="text"
                value={settings.contactPhone || ''}
                onChange={(e) => handleChange('contactPhone', e.target.value)}
                placeholder="+33 6 74 42 29 57"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('admin.settings.contactAddressLine1') || 'Address Line 1'}</Form.Label>
              <Form.Control
                type="text"
                value={settings.contactAddressLine1 || ''}
                onChange={(e) => handleChange('contactAddressLine1', e.target.value)}
                placeholder="2B Rue Alfred Nobel"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('admin.settings.contactAddressLine2') || 'Address Line 2'}</Form.Label>
              <Form.Control
                type="text"
                value={settings.contactAddressLine2 || ''}
                onChange={(e) => handleChange('contactAddressLine2', e.target.value)}
                placeholder="77420 Champs-sur-Marne, France"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('admin.settings.businessHours') || 'Business Hours'}</Form.Label>
              <Form.Control
                type="text"
                value={settings.businessHours || ''}
                onChange={(e) => handleChange('businessHours', e.target.value)}
                placeholder="Monday – Friday: 9:00 AM – 6:00 PM (CET)"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('admin.settings.mapEmbedUrl') || 'Map Embed URL'}</Form.Label>
              <Form.Control
                type="text"
                value={settings.mapEmbedUrl || ''}
                onChange={(e) => handleChange('mapEmbedUrl', e.target.value)}
                placeholder="https://maps.google.com/maps?q=..."
              />
            </Form.Group>
          </Col>
        </Row>

        <h5 className="mt-4 mb-3">{t('admin.settings.socialTitle') || 'Social Media'}</h5>
        <Row className="gy-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('admin.settings.socialLinkedin') || 'LinkedIn URL'}</Form.Label>
              <Form.Control
                type="text"
                value={settings.socialLinkedin || ''}
                onChange={(e) => handleChange('socialLinkedin', e.target.value)}
                placeholder="https://linkedin.com/company/..."
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('admin.settings.socialFacebook') || 'Facebook URL'}</Form.Label>
              <Form.Control
                type="text"
                value={settings.socialFacebook || ''}
                onChange={(e) => handleChange('socialFacebook', e.target.value)}
                placeholder="https://facebook.com/..."
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('admin.settings.socialTwitter') || 'Twitter URL'}</Form.Label>
              <Form.Control
                type="text"
                value={settings.socialTwitter || ''}
                onChange={(e) => handleChange('socialTwitter', e.target.value)}
                placeholder="https://twitter.com/..."
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('admin.settings.socialInstagram') || 'Instagram URL'}</Form.Label>
              <Form.Control
                type="text"
                value={settings.socialInstagram || ''}
                onChange={(e) => handleChange('socialInstagram', e.target.value)}
                placeholder="https://instagram.com/..."
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('admin.settings.socialWhatsapp') || 'WhatsApp Number'}</Form.Label>
              <Form.Control
                type="text"
                value={settings.socialWhatsapp || ''}
                onChange={(e) => handleChange('socialWhatsapp', e.target.value)}
                placeholder="+33 6 74 42 29 57"
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="mt-4">
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                {t('admin.settings.saving') || 'Saving...'}
              </>
            ) : (
              t('admin.settings.save') || 'Save Settings'
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
}
